import { CHAT_MESSAGE_DIRECTIONS, CHAT_MESSAGE_FAILED_REASONS, CHAT_MESSAGE_STATUSES } from 'cpool-constants';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { IconType } from 'react-icons';
import { LuCheck, LuCheckCheck, LuClock, LuTriangleAlert } from 'react-icons/lu';

import { MessageAttachment } from './MessageAttachment';
import { QuotedMessage } from './QuotedMessage';
import { TemplateButtonsRow } from './TemplateButtonsRow';
import { WhatsAppFormattedText } from '~/features/whatsapp/components/WhatsAppFormattedText';
import type { ChatMessage } from '~/features/whatsapp/types/message';
import { getVisibleChatMediaCaption } from '~/features/whatsapp/utils/resolveChatAttachmentDownloadName';
import { timeFormat } from '~/lib/i18n';
import { cn } from '~/utils/cnTailwind';

const STATUS_ICON: Partial<Record<ChatMessage['status'], IconType>> = {
    [CHAT_MESSAGE_STATUSES.DELIVERED]: LuCheckCheck,
    [CHAT_MESSAGE_STATUSES.FAILED]: LuTriangleAlert,
    [CHAT_MESSAGE_STATUSES.QUEUED]: LuClock,
    [CHAT_MESSAGE_STATUSES.READ]: LuCheckCheck,
    [CHAT_MESSAGE_STATUSES.SENT]: LuCheck,
};

type Props = {
    message: ChatMessage;
    // Resolved by the caller from the loaded page; absent if not a reply or original scrolled out.
    quotedMessage?: ChatMessage;
    // Supplied by the combined BOTH view — a chatId is 1:1 per phone, so the label must come from the caller.
    senderLabel?: string;
};

export const MessageBubble = ({ message, quotedMessage, senderLabel }: Props) => {
    const { t } = useTranslation();
    const isOutbound = message.direction === CHAT_MESSAGE_DIRECTIONS.OUTBOUND;
    const StatusIcon = STATUS_ICON[message.status];
    // media never reached S3 — requesting it would 404 on every render
    const hasUnavailableMedia = message.failureReason === CHAT_MESSAGE_FAILED_REASONS.MEDIA_FETCH_FAILED;
    const mediaCaption = getVisibleChatMediaCaption(message.content);
    const failureText =
        message.failureReason === CHAT_MESSAGE_FAILED_REASONS.SENDER_NUMBER_UNAVAILABLE
            ? t('WHATSAPP_CHAT.MESSAGE.SENDER_NUMBER_UNAVAILABLE')
            : message.failureReasonDetail;

    return (
        <div className={cn('flex px-4 py-1', isOutbound ? 'justify-end' : 'justify-start')}>
            <div
                className={cn(
                    'relative max-w-[80%] rounded-lg px-3 py-2 text-sm text-midnight',
                    // More saturated than Figma's inbound #E9EEF8 — that sat <4% from bg-blue-cloud.
                    isOutbound ? 'bg-purple-light' : 'bg-blue-cloud',
                )}
            >
                {message.reactionEmoji && (
                    <span
                        className={cn(
                            'absolute -bottom-2 flex size-5 items-center justify-center rounded-full border border-outline-light bg-white text-xs shadow-sm',
                            isOutbound ? 'right-1' : 'left-1',
                        )}
                        title={t('WHATSAPP_CHAT.MESSAGE.REACTION_LABEL', { emoji: message.reactionEmoji })}
                    >
                        {message.reactionEmoji}
                    </span>
                )}
                {senderLabel && <div className="mb-1 text-xs font-semibold text-blue">{senderLabel}</div>}
                {quotedMessage && <QuotedMessage quoted={quotedMessage} />}
                {message.header && (
                    <div className="mb-1 whitespace-pre-wrap break-words font-semibold">
                        <WhatsAppFormattedText text={message.header} />
                    </div>
                )}
                {message.mediaKey && message.mediaType && !hasUnavailableMedia ? (
                    <>
                        <MessageAttachment
                            chatId={message.chatId}
                            mediaFileName={message.mediaFileName}
                            mediaKey={message.mediaKey}
                            mediaType={message.mediaType}
                            name={message.content ?? undefined}
                        />
                        {mediaCaption && (
                            <div className="mt-1 whitespace-pre-wrap break-words">
                                <WhatsAppFormattedText text={mediaCaption} />
                            </div>
                        )}
                    </>
                ) : (
                    // without break-words, long URLs overflow the bubble and the whole list scrolls
                    <div className="whitespace-pre-wrap break-words">
                        <WhatsAppFormattedText text={message.content ?? ''} />
                    </div>
                )}
                {message.status === CHAT_MESSAGE_STATUSES.FAILED && failureText && (
                    <div className="mt-1 text-xs text-alert-error">{failureText}</div>
                )}
                <div className="mt-1 flex items-center justify-end gap-1 text-xs text-grey">
                    {timeFormat(new Date(message.timestamp))}
                    {isOutbound && StatusIcon && (
                        <StatusIcon
                            className={cn(
                                'size-3.5',
                                message.status === CHAT_MESSAGE_STATUSES.READ && 'text-blue',
                                message.status === CHAT_MESSAGE_STATUSES.FAILED && 'text-alert-error',
                            )}
                            aria-hidden
                            title={t(`WHATSAPP_CHAT.MESSAGE_STATUS.${message.status.toUpperCase()}` as ParseKeys)}
                        />
                    )}
                </div>
                {message.buttons && <TemplateButtonsRow buttons={message.buttons} />}
            </div>
        </div>
    );
};
